import type { TerminalEvent, TerminalRefs } from '@developerz/domain';
import { IconArrowRight } from '@developerz/ui';
import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { t } from '../../i18n';
import { orgHref } from '../../lib/org';
import type { TerminalTimeMode } from '../../lib/terminal/use-terminal-panes';
import { actorLabel, levelLabel, terminalLineText } from '../../lib/terminal-events';
import { absolute, ago, clock } from '../../lib/time';

// One backfilled line (#4395, epic #4391): `HH:MM:SS  LEVEL  actor@subject
// message  → link`. A presenter — `Terminal.tsx` owns the data, the window and
// the filter; this owns one row's grammar.
//
// THE "→ LINK" IS THE MOST SPECIFIC REF THE ROW HAS, in `wire/terminal.ts`'s
// own stated priority: a run timeline beats a plan (a task's fallback when no
// run), which beats the session it happened in, which beats the repo it
// touched, which beats a blocker. At most one link renders — a row about a
// run is not ALSO about "its session" for navigation purposes.
//
// "VIEW RAW" IS NOT `ActionDrawer` HERE, AND THAT IS A DELIBERATE GAP, not an
// oversight: `TerminalEvent` carries `has_raw` (from `visible_to_maintainer`)
// but never the raw payload itself (`wire/terminal.ts`'s own header — the
// SELECT withholds it structurally), and no `GET` exists yet to fetch one
// event's raw input/output by id. Opening `ActionDrawer` with nothing to show
// would be worse than not offering it. The full session timeline
// (`/audit/:session_id`, `AuditTimeline.tsx`) already renders the SAME raw
// JSON for every maintainer-visible row in it, so a `has_raw` line links there
// instead — real data today, and the natural spot for a per-event raw fetch
// to land later without moving this affordance.

function linkHref(refs: TerminalRefs): string | null {
  if (refs.runId !== undefined) return orgHref(`/fleet/runs/${refs.runId}`);
  if (refs.planId !== undefined) return orgHref(`/fleet/plans/${refs.planId}`);
  if (refs.sessionId !== undefined) return orgHref(`/audit/${refs.sessionId}`);
  if (refs.repoFullName !== undefined) {
    return orgHref(`/repos/${encodeURIComponent(refs.repoFullName)}/pending`);
  }
  if (refs.blockerId !== undefined) return orgHref(`/blockers?blocker=${refs.blockerId}`);
  return null;
}

/** How a row reads against the pane's search (#4401): a match, THE current match, or neither. */
export type TerminalRowMatch = 'match' | 'active' | null;

export function TerminalRow(props: {
  readonly event: TerminalEvent;
  /** Window-wide timestamp style (#4401). Absent = the wall clock #4395 shipped. */
  readonly timeMode?: TerminalTimeMode | undefined;
  readonly match?: TerminalRowMatch | undefined;
}): JSX.Element {
  const link = (): string | null => linkHref(props.event.refs);
  const rawHref = (): string | null =>
    props.event.has_raw && props.event.refs.sessionId !== undefined
      ? orgHref(`/audit/${props.event.refs.sessionId}`)
      : null;

  return (
    <div
      class="terminal__row"
      classList={{
        'terminal__row--match': props.match === 'match',
        'terminal__row--match-active': props.match === 'active',
      }}
      aria-current={props.match === 'active' ? 'true' : undefined}
    >
      {/* A real `<time>` (#4401): `datetime` for machines, the absolute
          stamp as `title` for a hover, whichever style is showing — the same
          contract `AuditFeedList.tsx` keeps. */}
      <time class="terminal__row-ts" datetime={props.event.ts} title={absolute(props.event.ts)}>
        {props.timeMode === 'relative' ? ago(props.event.ts) : clock(props.event.ts)}
      </time>
      <span class={`terminal__row-level terminal__row-level--${props.event.level}`}>
        {levelLabel(props.event.level)}
      </span>
      <span class="terminal__row-actor">
        {actorLabel(props.event.actor)}@{props.event.subject.label}
      </span>
      <span class="terminal__row-message">{terminalLineText(props.event.line)}</span>
      <Show when={link()}>
        {(href) => (
          <a href={href()} class="terminal__row-link" aria-label={t('terminal.open_link')}>
            <IconArrowRight size="0.75rem" />
          </a>
        )}
      </Show>
      <Show when={rawHref()}>
        {(href) => (
          <a href={href()} class="terminal__row-expand">
            {t('terminal.view_raw')}
          </a>
        )}
      </Show>
    </div>
  );
}
